<template>
    <div class="modal fade" tabindex="-1" id="downloadReportModal">
        <div class="modal-dialog">
            <div class="modal-content bg-white">
                <div class="modal-header">
                    <h5 class="modal-title">Download Reports</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body pt-0">
                    <!-- Bordered Tabs -->
                    <ul class="nav nav-tabs nav-tabs-bordered" id="reportTab" role="tablist">
                        <li class="nav-item" role="presentation">
                            <button class="nav-link active" id="order-tab" data-bs-toggle="tab" data-bs-target="#filter-order" type="button" role="tab" aria-controls="order" aria-selected="true">Order</button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" id="duedates-tab" data-bs-toggle="tab" data-bs-target="#filter-duedates" type="button" role="tab" aria-controls="duedates" aria-selected="false" tabindex="-1">Due Dates</button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link" id="handymans-tab" data-bs-toggle="tab" data-bs-target="#filter-handymans" type="button" role="tab" aria-controls="handymans" aria-selected="false" tabindex="-1">Handyman</button>
                        </li>
                    </ul>
                    <div class="tab-content pt-2" id="reportTabContent">
                        <div class="tab-pane fade active show" id="filter-order" role="tabpanel" aria-labelledby="order-tab">
                            <form id="filterForm">
                                <div class="filter-section">
                                    <div class="mb-3">
                                        <label class="form-label">Select Filter Types:</label>
                                        <div class="checkbox-group d-flex flex-wrap gap-3">
                                            <div class="form-check" v-for="(option, key) in filterOptions" :key="key">
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    :id="key + 'Check'"
                                                    v-model="selectedFilters[key]"
                                                    :disabled="key === 'handymans'"
                                                />
                                                <label class="form-check-label" :for="key + 'Check'">
                                                    {{ option.label }}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Dynamic additional options will change based on selection -->
                                    <div class="additional-options">
                                        <!-- Orders Option -->
                                        <transition name="fade">
                                            <div v-if="selectedFilters.orders" class="mb-3">
                                                <label for="statusSelect" class="form-label">Status:</label>
                                                <VueSelect
                                                    v-model="filters.status"
                                                    :items="filterOptions.orders.options"
                                                    :searchable="true"
                                                    :multiple="true"
                                                    :placeholder="'Select Status'"
                                                    @onSelectOption="handleStatusSelect"
                                                ></VueSelect>
                                            </div>
                                        </transition>

                                        <!-- Customers Option -->
                                        <transition name="fade">
                                            <div v-if="selectedFilters.customers" class="mb-3">
                                                <label for="propertiesSelect" class="form-label">Customers:</label>
                                                <VueSelect
                                                    v-model="filters.customers"
                                                    :items="filterOptions.customers.options"
                                                    :searchable="true"
                                                    :multiple="true"
                                                    :placeholder="'Select Customers'"
                                                    @onSelectOption="handleCustomerSelect"
                                                    @onRemoveOption="handleCustomerRemove"
                                                ></VueSelect>
                                            </div>
                                        </transition>

                                        <!-- Display customer properties -->
                                        <transition name="fade">
                                            <div v-if="selectedFilters.customers && filters.customers" class="mb-3">
                                                <label for="propertiesSelect" class="form-label">Properties:</label>
                                                <vue-multiselect
                                                    v-model="filters.properties"
                                                    class="w-100"
                                                    :options="filterOptions.customers.properties"
                                                    :placeholder="'Select Properties'"
                                                    :show-labels="false"
                                                    track-by="name"
                                                    label="name"
                                                    :multiple="true"
                                                    :searchable="true"
                                                    :group-select="false"
                                                    group-values="properties"
                                                    group-label="group"
                                                    @select="handlePropertySelect($event)"
                                                ></vue-multiselect>
                                            </div>
                                        </transition>

                                        <!-- Handymans Option -->
                                        <transition name="fade">
                                            <div v-if="selectedFilters.handymans" class="mb-3">
                                                <label for="handymanSelect" class="form-label">Handyman Name:</label>
                                                <VueSelect
                                                    v-model="filters.handymans"
                                                    :items="filterOptions.handymans.options"
                                                    :searchable="false"
                                                    :multiple="true"
                                                    :placeholder="'Select Handyman'"
                                                ></VueSelect>
                                            </div>
                                        </transition>
                                    </div>
                                </div>

                                <!-- Date Range (visible for all options) -->
                                <div class="date-range-container">
                                    <div class="mb-3">
                                        <label for="dateRange" class="form-label">Date Range</label>
                                        <VueSelect
                                            v-model="filters.date_type"
                                            :items="[
                                                { id: 'last_30_days', name: 'Last 30 Days' },
                                                { id: 'this_month', name: 'This Month' },
                                                { id: 'last_month', name: 'Last Month' },
                                                { id: 'this_year', name: 'This Year' },
                                                { id: 'last_year', name: 'Last Year' },
                                                { id: 'custom', name: 'Custom' }
                                            ]"
                                            :searchable="false"
                                            :multiple="false"
                                            :placeholder="'Select Date Range'"
                                        ></VueSelect>
                                    </div>
                                    <div v-if="filters.date_type==='custom'" class="row g-3">
                                        <div class="col-md-6">
                                            <label for="startDate" class="form-label">Start Date:</label>
                                            <VueDatePicker
                                                v-model="filters.start_date"
                                                :enable-time-picker="false"
                                                :clearable="false"
                                                :format="'dd-MM-yyyy'"
                                                :timezone="'UTC'"
                                            />
                                        </div>
                                        <div class="col-md-6">
                                            <label for="endDate" class="form-label">End Date:</label>
                                            <VueDatePicker
                                                v-model="filters.end_date"
                                                :enable-time-picker="false"
                                                :clearable="false"
                                                :format="'dd-MM-yyyy'"
                                                :timezone="'UTC'"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="d-flex justify-content-end mt-3">
                                    <button type="button" class="btn btn-sm btn-secondary me-2" data-bs-dismiss="modal">Close</button>
                                    <template v-if="filterEnabled">
                                        <button v-if="!processing" type="button" class="btn btn-sm btn-theme me-2" @click="downloadReport">
                                            <i class="bi bi-arrow-down-circle pe-1"></i> Download
                                        </button>
                                        <button v-else type="button" class="btn btn-sm btn-outline-theme ms-2 disabled" disabled>
                                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </button>
                                    </template>
                                </div>
                            </form>
                        </div>
                        <div class="tab-pane fade" id="filter-duedates" role="tabpanel" aria-labelledby="duedates-tab">
                            <form id="filterDuedatesForm">
                                <div class="filter-section">
                                    <div class="additional-options">
                                        <!-- Customers Option -->
                                        <transition name="fade">
                                            <div class="mb-3">
                                                <label for="propertiesSelect" class="form-label">Customers:</label>
                                                <VueSelect
                                                    v-model="filters.customers"
                                                    :items="filterOptions.customers.options"
                                                    :searchable="true"
                                                    :multiple="true"
                                                    :placeholder="'Select Customers'"
                                                    @onSelectOption="handleCustomerSelect"
                                                    @onRemoveOption="handleCustomerRemove"
                                                ></VueSelect>
                                            </div>
                                        </transition>

                                        <!-- Display customer properties -->
                                        <transition name="fade">
                                            <div v-if="filters.customers" class="mb-3">
                                                <label for="propertiesSelect" class="form-label">Properties:</label>
                                                <vue-multiselect
                                                    v-model="filters.properties"
                                                    class="w-100"
                                                    :options="filterOptions.customers.properties"
                                                    :placeholder="'Select Properties'"
                                                    :show-labels="false"
                                                    track-by="name"
                                                    label="name"
                                                    :multiple="true"
                                                    :searchable="true"
                                                    :group-select="false"
                                                    group-values="properties"
                                                    group-label="group"
                                                    @select="handlePropertySelect($event)"
                                                ></vue-multiselect>
                                            </div>
                                        </transition>
                                    </div>
                                </div>

                                <!-- Date Range (visible for all options) -->
                                <div class="date-range-container">
                                    <div class="mb-3">
                                        <label for="dateRange" class="form-label">Date Range</label>
                                        <VueSelect
                                            v-model="filters.date_type"
                                            :items="[
                                                { id: 'last_30_days', name: 'Last 30 Days' },
                                                { id: 'this_month', name: 'This Month' },
                                                { id: 'last_month', name: 'Last Month' },
                                                { id: 'this_year', name: 'This Year' },
                                                { id: 'last_year', name: 'Last Year' },
                                                { id: 'custom', name: 'Custom' }
                                            ]"
                                            :searchable="false"
                                            :multiple="false"
                                            :placeholder="'Select Date Range'"
                                        ></VueSelect>
                                    </div>
                                    <div v-if="filters.date_type==='custom'" class="row g-3">
                                        <div class="col-md-6">
                                            <label for="startDate" class="form-label">Start Date:</label>
                                            <VueDatePicker
                                                v-model="filters.start_date"
                                                :enable-time-picker="false"
                                                :clearable="false"
                                                :format="'dd-MM-yyyy'"
                                                :timezone="'UTC'"
                                                :auto-apply="true"
                                            />
                                        </div>
                                        <div class="col-md-6">
                                            <label for="endDate" class="form-label">End Date:</label>
                                            <VueDatePicker
                                                v-model="filters.end_date"
                                                :enable-time-picker="false"
                                                :clearable="false"
                                                :format="'dd-MM-yyyy'"
                                                :timezone="'UTC'"
                                                :auto-apply="true"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="d-flex justify-content-end mt-3">
                                    <button type="button" class="btn btn-sm btn-secondary me-2" data-bs-dismiss="modal">Close</button>
                                    <button v-if="!processing" type="button" class="btn btn-sm btn-theme me-2" @click="downloadDueDateReport">
                                        <i class="bi bi-arrow-down-circle pe-1"></i> Download
                                    </button>
                                    <button v-else type="button" class="btn btn-sm btn-outline-theme ms-2 disabled" disabled>
                                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div class="tab-pane fade" id="filter-handymans" role="tabpanel" aria-labelledby="handymans-tab">
                            <form id="handymanFilterForm">
                                <div class="filter-section">
                                    <div class="additional-options">
                                        <!-- Handyman Option -->
                                        <transition name="fade">
                                            <div class="mb-3">
                                                <label for="handymanSelect" class="form-label">Handyman:</label>
                                                <VueSelect
                                                    v-model="handymanReports.filters.handymans"
                                                    :items="handymanReports.options.handymans"
                                                    :searchable="true"
                                                    :multiple="true"
                                                    :placeholder="'Select Handyman'"
                                                    @onSelectOption="handleHandymanSelect"
                                                    @onRemoveOption="handleHandymanRemove"
                                                ></VueSelect>
                                            </div>
                                        </transition>
                                        <!-- Customers Option -->
                                        <transition name="fade">
                                            <div class="mb-3">
                                                <label for="handymanCustomersSelect" class="form-label">Customers:</label>
                                                <VueSelect
                                                    v-model="handymanReports.filters.customers"
                                                    :items="handymanReports.options.customers"
                                                    :searchable="true"
                                                    :multiple="true"
                                                    :placeholder="'Select Customers'"
                                                    @onSelectOption="handleHandymanCustomerSelect"
                                                    @onRemoveOption="handleHandymanCustomerRemove"
                                                ></VueSelect>
                                            </div>
                                        </transition>

                                        <!-- Display customer properties -->
                                        <transition name="fade">
                                            <div v-if="handymanReports.filters.customers" class="mb-3">
                                                <label for="propertiesSelect" class="form-label">Properties:</label>
                                                <vue-multiselect
                                                    v-model="handymanReports.filters.properties"
                                                    class="w-100"
                                                    :options="handymanReports.options.properties"
                                                    :placeholder="'Select Properties'"
                                                    :show-labels="false"
                                                    track-by="name"
                                                    label="name"
                                                    :multiple="true"
                                                    :searchable="true"
                                                    :group-select="false"
                                                    group-values="properties"
                                                    group-label="group"
                                                    @select="handleHandymanPropertySelect($event)"
                                                ></vue-multiselect>
                                            </div>
                                        </transition>
                                    </div>
                                </div>

                                <!-- Date Range (visible for all options) -->
                                <div class="date-range-container">
                                    <div class="mb-3">
                                        <label for="dateRange" class="form-label">Date Range</label>
                                        <VueSelect
                                            v-model="handymanReports.filters.date_type"
                                            :items="[
                                                { id: 'last_30_days', name: 'Last 30 Days' },
                                                { id: 'this_month', name: 'This Month' },
                                                { id: 'last_month', name: 'Last Month' },
                                                { id: 'this_year', name: 'This Year' },
                                                { id: 'last_year', name: 'Last Year' },
                                                { id: 'custom', name: 'Custom' }
                                            ]"
                                            :searchable="false"
                                            :multiple="false"
                                            :placeholder="'Select Date Range'"
                                        ></VueSelect>
                                    </div>
                                    <div v-if="handymanReports.filters.date_type==='custom'" class="row g-3">
                                        <div class="col-md-6">
                                            <label for="startDate" class="form-label">Start Date:</label>
                                            <VueDatePicker
                                                v-model="handymanReports.filters.start_date"
                                                :enable-time-picker="false"
                                                :clearable="false"
                                                :format="'dd-MM-yyyy'"
                                                :timezone="'UTC'"
                                                :auto-apply="true"
                                            />
                                        </div>
                                        <div class="col-md-6">
                                            <label for="endDate" class="form-label">End Date:</label>
                                            <VueDatePicker
                                                v-model="handymanReports.filters.end_date"
                                                :enable-time-picker="false"
                                                :clearable="false"
                                                :format="'dd-MM-yyyy'"
                                                :timezone="'UTC'"
                                                :auto-apply="true"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="d-flex justify-content-end mt-3">
                                    <button type="button" class="btn btn-sm btn-secondary me-2" data-bs-dismiss="modal">Close</button>
                                    <button v-if="!processing" type="button" class="btn btn-sm btn-theme me-2" @click="downloadHandymanReport">
                                        <i class="bi bi-arrow-down-circle pe-1"></i> Download
                                    </button>
                                    <button v-else type="button" class="btn btn-sm btn-outline-theme ms-2 disabled" disabled>
                                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import axios from 'axios';
import { appMixin } from '../../mixin/appMixin';
import VueSelect from './VueSelect';

export default {
    name: 'FilterReports',
    mixins: [appMixin],
    components: {
        VueSelect
    },
    data() {
        return {
            processing: false,
            filterOptions: {
                orders: {
                    label: 'Orders',
                    options: [
                        { id: 'all', name: 'Select All' },
                        { id: 'pending', name: 'Pending' },
                        { id: 'assigned', name: 'Assigned' },
                        { id: 'processing', name: 'Processing' },
                        { id: 'quotation', name: 'Quotation' },
                        { id: 'engineer_completed', name: 'Engineer Completed' },
                        { id: 'completed', name: 'Completed' },
                        { id: 'cancelled', name: 'Cancelled' },
                    ]
                },
                customers: {
                    label: 'Customers',
                    options: [],
                    properties: []
                },
                handymans: {
                    label: 'Handymans (coming soon)',
                    options: []
                }
            },
            filters: {
                status: [],
                customers: [],
                properties: [],
                handymans: [],
                start_date: this.moment().subtract(30, 'day'),
                end_date: this.moment(),
                date_type: 'last_30_days', // last_30_days, this_month, last_month, this_year, last_year, custom
            },
            selectedFilters: {
                orders: true,
                customers: false,
                handymans: false,
            },
            orders: [],
            customers: [],
            properties: [],
            handymans: [],
            handymanReports: {
                handymans: [],
                options: {
                    handymans: [],
                    customers: [],
                    properties: [],
                },
                filters: {
                    handymans: [],
                    customers: [],
                    properties: [],
                    start_date: this.moment().subtract(30, 'day'),
                    end_date: this.moment(),
                    date_type: 'last_30_days', // last_30_days, this_month, last_month, this_year, last_year, custom
                },
            }
        };
    },
    created() {
        this.getOrderCustomers();

        // Get handyman data
        this.getHandymanData();
    },
    computed: {
        filterEnabled() {
            // Check at-least one filter is enabled
            return Object.values(this.selectedFilters).some(value => value);
        }
    },
    methods: {
        getOrderCustomers() {
            let self = this;

            axios.post('/api/admin/order-customers')
                .then(response => {
                    self.customers = response.data.customers;

                    // Prepare options
                    self.filterOptions.customers.options = [{id: 'all', name: 'Select All'}];
                    self.filterOptions.customers.options.push(...response.data.customers.map(customer => ({
                        id: customer.id,
                        name: customer.name
                    })));

                    // self.filterOptions.customers.options = response.data.customers.map(customer => ({
                    //     id: customer.id,
                    //     name: customer.name
                    // }));
                })
                .catch(error => {
                    console.error(error);
                });
        },
        downloadReport() {
            let self = this;
            this.processing = true;

            axios.post('/api/admin/download-report', this.filters)
                .then(response => {
                    self.processing = false;
                    self.orders = response.data.orders;
                    self.exportToCSV();
                })
                .catch(error => {
                    self.processing = false;
                });
        },
        exportToCSV() {
            // CSV header
            const headers = [
                'Handyman Name',
                'Order Number',
                'Address',
                'Service Name',
                'Customer Name',
                'Customer Phone',
                'Customer Email',
                'Access Arrangement',
                'Service Date',
                'Service Time',
                'Duration (hours)',
            ];

            // let csvContent = headers.join(',') + '\n';
            // this.orders.forEach(order => {
            //     const row = [
            //         `"${order?.handymans?.map(h => h.name).join(', ')}"`,
            //         `"${order.order_number}"`,
            //         `"${order.booking_address}"`,
            //         `"${order?.services?.map(s => s.name).join(', ')}"`,
            //         `"${order.customer.name}"`,
            //         `"${order.customer.phone_number}"`,
            //         `"${order.customer.email}"`,
            //         `"${this.dataTimeFormat(order.booking_date, 'YYYY-MM-DD')}"`,
            //         `"${this.dataTimeFormat(order.booking_date, 'hh:mm a')}"`,
            //         `"${order.assigned_order_duration || ''}"`,
            //     ];
            //     csvContent += row.join(',') + '\n';
            // });

            // Create CSV content in chunks
            const chunkSize = 1000; // Process 1000 rows at a time
            let csvContent = headers.join(',') + '\n';

            for (let i = 0; i < this.orders.length; i += chunkSize) {
                const chunk = this.orders.slice(i, i + chunkSize);
                csvContent += chunk.map(order => {
                    return [
                        `"${order?.handymans?.map(h => h.name).join(', ') || ''}"`,
                        `"${order.order_number || ''}"`,
                        `"${order.booking_address || ''}"`,
                        `"${order?.services?.map(s => s.name).join(', ') || ''}"`,
                        `"${order.customer?.name || ''}"`,
                        `"${order.customer?.phone_number || ''}"`,
                        `"${order.customer?.email || ''}"`,
                        `"${order.access_arrangement || ''}"`,
                        `"${this.dataTimeFormat(order.booking_date, 'YYYY-MM-DD') || ''}"`,
                        `"${this.dataTimeFormat(order.booking_date, 'hh:mm a') || ''}"`,
                        `"${order.assigned_order_duration || ''}"`
                    ].join(',');
                }).join('\n') + '\n';
            }

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'reports.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        prepareServiceName(items) {
            return items.map(item => item.service.name).join(', ');
        },
        handleStatusSelect(option) {
            // Check if option.id === 'all' then remove all other selected options
            if(option.id === 'all') {
                this.filters.status = ['all']
            }
            else {
                // Remove all if selected
                this.filters.status = this.filters.status.filter(id => id !== 'all');
            }

            // Check if select all then select all options
            // Except select all or remove all option
            // If selected option id is remove then remove all options
            
            /* if(option.id === 'all') {
                this.filters.status = this.filterOptions.orders.options.map(option => option.id).filter(id => id !== 'all' && id !== 'remove-all');
            }
            else if(option.id === 'remove-all') {
                this.filters.status = [];

                // Remove option remove all from the end
                this.filterOptions.orders.options = this.filterOptions.orders.options.filter(option => option.id !== 'remove-all');
            }

            // If filters.status is not empty then add the remove all option from the end
            // Push only if remove all option is not already present
            if(this.filters.status.length > 0 && !this.filterOptions.orders.options.some(option => option.id === 'remove-all')) {
                this.filterOptions.orders.options.push({
                    id: 'remove-all',
                    name: 'Remove All'
                });
            } */
        },
        handleCustomerSelect(option) {
            // Check if option.id === 'all' then remove all other selected options
            if(option.id === 'all') {
                this.filters.customers = ['all']
            }
            else {
                // Remove all if selected
                this.filters.customers = this.filters.customers.filter(id => id !== 'all');
            }
            this.preparePropertiesOptions();

            // Check if select all then select all options
            // Except select all or remove all option
            // If selected option id is remove then remove all options

            /* if(option.id === 'all') {
                this.filters.customers = this.filterOptions.customers.options.map(option => option.id).filter(id => id !== 'all' && id !== 'remove-all');
            }
            else if(option.id === 'remove-all') {
                this.filters.customers = [];

                // Remove option remove all from the end
                this.filterOptions.customers.options = this.filterOptions.customers.options.filter(option => option.id !== 'remove-all');
            }

            // Prepare the properties options for the selected customers
            this.preparePropertiesOptions();


            // If filters.customers is not empty then add the remove all option from the end
            // Push only if remove all option is not already present
            if(this.filters.customers.length > 0 && !this.filterOptions.customers.options.some(option => option.id === 'remove-all')) {
                this.filterOptions.customers.options.push({
                    id: 'remove-all',
                    name: 'Remove All'
                });
            } */
        },
        handleCustomerRemove(option) {
            // Check if this.filters.customers is empty then remove the selected properties
            if(this.filters.customers.length === 0 || option.id === 'all') {
                this.filters.properties = [];
            }
            // find the removed id from the selected properties
            else {
                this.filters.properties = this.filters.properties.filter(property => property.customer_id !== option.id);
            }
            this.preparePropertiesOptions();
        },
        preparePropertiesOptions() {
            // Check all customer is selected
            let allCustomers = [];
            if(this.filters.customers.includes('all')) {
                allCustomers = this.filterOptions.customers.options.map(option => option.id).filter(id => id !== 'all' && id !== 'remove-all');
            }
            else {
                allCustomers = this.filters.customers;
            } 
            
            const selectedCustomers = this.customers.filter(customer =>
                allCustomers.includes(customer.id)
            );
            // Prepare options with global Select All and customer groups
            this.filterOptions.customers.properties = [
                // Global Select All option
                {
                    group: 'Select Properties',
                    properties: [{name: 'Select All', id: 'all', customer_id: 'all'}]
                },
                // Customer groups
                ...selectedCustomers.map(customer => ({
                    group: customer.name,
                    properties: customer.addresses.map(address => ({
                        name: address.full_address,
                        id: address.id,
                        customer_id: customer.id
                    }))
                }))
            ];
        },
        handlePropertySelect(option) {
            const selectedOption = option[0]?.id || option.id;

            if(selectedOption === 'all') {
                this.filters.properties = [{id: 'all', name: 'Select All'}]
            }
            else {
                // Remove all if selected
                this.filters.properties = this.filters.properties.filter(property => property.id !== 'all');
            }

            // Check if select all then select all options
            // Except select all or remove all option
            // If selected option id is remove then remove all options
            /* if(selectedOption === 'all') {
                let allCustomers = this.filterOptions.customers.options.map(option => option.id).filter(id => id !== 'all' && id !== 'remove-all');
                const selectedCustomers = this.customers.filter(customer =>
                    allCustomers.includes(customer.id)
                );
                const allProperties = selectedCustomers
                    .flatMap(customer => customer.addresses)
                    .map(address => ({
                        id: address.id,
                        name: address.full_address
                    }));
                this.filters.properties = allProperties;
            }
            else if(selectedOption === 'remove-all') {
                this.filters.properties = [];

                // Prepare the properties options for the selected customers
                this.preparePropertiesOptions();
            }

            // If filters.properties is not empty then add the remove all option group at the end
            // Push only if remove all option is not already present
            const removeAllOption = this.filterOptions.customers.properties.find(option => option.group === 'Remove Properties');
            if(this.filters.properties.length > 0 && !removeAllOption) {
                // Add the remove all option group
                this.filterOptions.customers.properties.push({
                    group: 'Remove Properties',
                    properties: [{name: 'Remove All', id: 'remove-all'}]
                });
            } */

        },
        downloadDueDateReport() {
            this.processing = true;
            axios.post('/api/admin/reports/due-dates', {
                customers: this.filters.customers,
                properties: this.filters.properties.map(property => property.id),
                start_date: this.filters.start_date,
                end_date: this.filters.end_date,
                date_type: this.filters.date_type,
            })
                .then(response => {
                    this.exportDueDatesCsv(response.data);
                })
                .catch(error => {
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        exportDueDatesCsv(responseData) {
            // Use columns from API response as headers
            const headers = responseData.columns || [];

            // Create CSV content in chunks
            const chunkSize = 1000; // Process 1000 rows at a time
            let csvContent = headers.join(',') + '\n';

            const properties = responseData.properties || [];

            for (let i = 0; i < properties.length; i += chunkSize) {
                const chunk = properties.slice(i, i + chunkSize);
                csvContent += chunk.map(property => {
                    // Dynamically build row data based on columns order
                    return headers.map(column => {
                        // Handle different column mappings
                        let value = '';
                        switch(column) {
                            case 'Property':
                                value = property.address || '';
                                break;
                            case 'Customer':
                                value = property.customer || '';
                                break;
                            case 'Email':
                                value = property.customer_email || '';
                                break;
                            case 'Phone':
                                value = property.customer_phone || '';
                                break;
                            default:
                                // For service columns, use the column name as key
                                value = property[column] || '';
                                break;
                        }
                        return `"${value}"`;
                    }).join(',');
                }).join('\n') + '\n';
            }

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'due_dates_report.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        getHandymanData() {
            axios.post('/api/admin/handyman-list')
                .then(response => {
                    this.handymanReports.handymans = response.data.handymans;

                    // Prepare handyman options
                    this.handymanReports.options.handymans = [{id: 'all', name: 'Select All', email: null}];
                    this.handymanReports.options.handymans.push(...response.data.handymans.map(handymans => ({
                        id: handymans.id,
                        name: handymans.name,
                        email: handymans.email,
                        phone: handymans.phone_number
                    })));
                })
                .catch(error => {
                    console.error('Error fetching handyman data:', error);
                });
        },
        handleHandymanSelect(option) {
            // Check if select all
            if(option.id === 'all') {
                this.handymanReports.filters.handymans = ['all'];

                // Reset customers and properties and its options
                this.handymanReports.filters.customers = [];
                this.handymanReports.filters.properties = [];

                this.handymanReports.options.customers = [];
                this.handymanReports.options.properties = [];
            }
            else {
                // Remove all if selected
                this.handymanReports.filters.handymans = this.handymanReports.filters.handymans.filter(id => id !== 'all');
            }

            this.getHandymanCustomers();
        },
        handleHandymanRemove() {
            // Check if selected handyman length is 0
            if(this.handymanReports.filters.handymans.length === 0) {
                // Reset customers and properties selected
                this.handymanReports.filters.customers = [];
                this.handymanReports.filters.properties = [];

                // Reset customers and properties options
                this.handymanReports.options.customers = [];
                this.handymanReports.options.properties = [];
            }
            else {
                this.getHandymanCustomers();
            }
        },
        getHandymanCustomers() {
             // Get the customers for the selected handyman
             axios.post('/api/admin/reports/handyman-customers', {
                handyman_ids: this.handymanReports.filters.handymans
            })
                .then(response => {
                    //this.handymanReports.options.customers = response.data.customers;
                    let customers = response.data.customers;
                    this.handymanReports.options.customers = [{id: 'all', name: 'Select All', email: null}];
                    this.handymanReports.options.customers.push(...customers.map(customer => ({
                        id: customer.id,
                        name: customer.name,
                        email: customer.email,
                        phone: customer.phone
                    })));

                })
                .catch(error => {
                    console.error('Error fetching handyman customers:', error);
                });
        },
        handleHandymanCustomerSelect(option) {
            // Check if select all
            if(option.id === 'all') {
                this.handymanReports.filters.customers = ['all'];
                this.handymanReports.options.properties = [];
            }
            else {
                // Remove all if selected
                this.handymanReports.filters.customers = this.handymanReports.filters.customers.filter(id => id !== 'all');
            }
            this.getHandymanCustomerProperties();
        },
        handleHandymanCustomerRemove() {
            // Check if selected customer length is 0
            if(this.handymanReports.filters.customers.length === 0) {
                // Reset properties selected
                this.handymanReports.filters.properties = [];

                // Reset properties options
                this.handymanReports.options.properties = [];
            }
            else {
                this.getHandymanCustomerProperties();
            }
        },
        getHandymanCustomerProperties() {
            // Get the properties for the selected handyman-customer combination
            axios.post('/api/admin/reports/handyman-properties', {
                handyman_ids: this.handymanReports.filters.handymans,
                customer_ids: this.handymanReports.filters.customers
            })
                .then(response => {
                    //this.handymanReports.options.properties = response.data.properties;
                    let properties = response.data.properties;
                    
                    // Prepare properties options with group by customer
                    let groupedProperties = [];
                    properties.forEach(property => {
                        let customer = property.user;
                        if (!groupedProperties.some(group => group.customer_id === customer.id)) {
                            groupedProperties.push({
                                customer_id: customer.id,
                                customer_name: customer.name,
                                properties: []
                            });
                        }
                        groupedProperties.find(group => group.customer_id === customer.id).properties.push({
                            name: property.full_address,
                            id: property.id,
                            customer_id: customer.id
                        });
                    });

                    this.handymanReports.options.properties = [
                        // Global Select All option
                        {
                            group: 'Select Properties',
                            properties: [{name: 'Select All', id: 'all', customer_id: 'all'}]
                        },
                        // Customer groups
                        ...groupedProperties.map(group => ({
                            group: group.customer_name,
                            properties: group.properties
                        }))
                    ];
                })
                .catch(error => {
                    console.error('Error fetching handyman customers:', error);
                });
        },
        handleHandymanPropertySelect(option) {
            // Check if select all
            if(option.id === 'all') {
                this.handymanReports.filters.properties =  [{id: 'all', name: 'Select All'}];
            }
            else {
                // Remove all if selected
                this.handymanReports.filters.properties = this.handymanReports.filters.properties.filter(id => id !== 'all');
            }
        },
        downloadHandymanReport() {
            this.processing = true;
            axios.post('/api/admin/reports/handyman-report', {
                handyman_ids: this.handymanReports.filters.handymans,
                customer_ids: this.handymanReports.filters.customers,
                property_ids: this.handymanReports.filters.properties,
                start_date: this.handymanReports.filters.start_date,
                end_date: this.handymanReports.filters.end_date,
                date_type: this.handymanReports.filters.date_type
            })
                .then(response => {
                    if(response.data.status === 'success') {
                        this.generateHandymanReportCsv(response.data.orders);
                    }
                })
                .catch(error => {
                    console.error('Error downloading handyman report:', error);
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        generateHandymanReportCsv(orders) {
            if(orders.length === 0) {
                this.$notification('error', 'No orders found');
                return;
            }

            const headers = [
                'Handyman Name',
                'Order Number',
                'Address',
                'Service Name',
                'Customer Name',
                'Customer Phone',
                'Customer Email',
                'Access Arrangement',
                'Service Date',
                'Service Time',
                'Duration (hours)',
                'Order Status'
            ];

            // Create CSV content in chunks
            const chunkSize = 1000; // Process 1000 rows at a time
            let csvContent = headers.join(',') + '\n';

            for (let i = 0; i < orders.length; i += chunkSize) {
                const chunk = orders.slice(i, i + chunkSize);
                csvContent += chunk.map(order => {
                    return [
                        `"${order?.handymans?.map(h => h.name).join(', ') || ''}"`,
                        `"${order.order_number || ''}"`,
                        `"${order.booking_address || ''}"`,
                        `"${order?.services?.map(s => s.name).join(', ') || ''}"`,
                        `"${order.customer?.name || ''}"`,
                        `"${order.customer?.phone_number || ''}"`,
                        `"${order.customer?.email || ''}"`,
                        `"${order.access_arrangement || ''}"`,
                        `"${this.dataTimeFormat(order.booking_date, 'dddd, MMMM D, YYYY') || ''}"`,
                        `"${this.dataTimeFormat(order.booking_date, 'hh:mm a') || ''}"`,
                        `"${order.assigned_order_duration || ''}"`,
                        `"${this.capitalize(order.status.replace(/_/g, ' ') || '')}"`
                    ].join(',');
                }).join('\n') + '\n';
            }

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'handyman-reports.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Reset the processing state and filter data
            this.processing = false;
            this.handymanReports.filters = {
                handymans: [],
                customers: [],
                properties: [],
                start_date: this.moment().subtract(30, 'day'),
                end_date: this.moment(),
                date_type: 'last_30_days',
            };

            // Close the modal
            $('#downloadReportModal').modal('hide');
        }
    },
    watch: {
        'filters.customers': function() {
            // Filter the properties based on selected customer
            // Return addresses of the selected customer

            /* const customer = this.customers.find(customer => customer.id === this.filters.customers);
            if(customer) {
                // Prepare properties options
                this.filterOptions.customers.properties = [{id: 'all', name: 'Select All'}];
                this.filterOptions.customers.properties.push(...customer.addresses.map(address => ({
                    id: address.id,
                    name: address.full_address
                })));
            } */
        }
    }
}
</script>
